import IframeModal from '../IframeModal';
import { isBrowser, isIE } from '@/helpers/BrowserHelper';
import React, {
  ReactElement,
  VFC,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';

const INICIS_BANK_MAP: { [key: string]: string } = {
  '039': `39`,
  '034': `34`,
  '261': `DI`,
  '003': `03`,
  '012': `12`,
  '031': `31`,
  '267': `D9`,
  '055': `55`,
  '287': `DE`,
  '238': `D3`,
  '290': `DO`,
  '032': `32`,
  '061': `61`,
  '064': `64`,
  '002': `02`,
  '240': `DH`,
  '045': `45`,
  '007': `07`,
  '053': `53`,
  '291': `DF`,
  '278': `DB`,
  '088': `88`,
  '048': `48`,
  '020': `20`,
  '071': `71`,
  '209': `D1`,
  '280': `DD`,
  '265': `DK`,
  '050': `50`,
  '037': `37`,
  '057': `57`,
  '035': `35`,
  '062': `62`,
  '090': `90`,
  '089': `89`,
  '292': `DP`, // 케이프투자증권 (일단 LIG증권으로 세팅중)
  '264': `DJ`,
  '270': `DA`,
  '081': `81`,
  '262': `D6`,
  '027': `27`,
  '293': `D4`, // 한국증권금융 (한국투자증권)
  '243': `D4`,
  '294': `D4`, // 한국포스증권 (한국투자증권)
  '269': `DM`,
  '263': `D7`,
  '060': `60`,
  '279': `DC`, // DB금융투자
  '054': `54`,
  '004': `04`,
  '218': `D2`,
  '011': `11`,
  '247': `DN`,
  '289': `DN`,
  '023': `23`,
  '266': `D8`,
  '092': `92`,
};

interface InicisResult {
  type: 'inicis-account-check';
  resultCode: string;
  resultMessage: string;
  name: string;
  isDecoded: boolean;
}

interface InicisParams {
  name: string;
  account: string;
  bankCode: string;
  oriAccountNumber: string;
  oriName: string;
 // accountType: string
}

interface InicisAccountCheckProps {
  onSuccess?: (result: InicisResult) => void;
  onError?: (message: string) => void;
  renderSubmitButton: (props: { onClick: () => void }) => ReactElement;
  getParams: () => InicisParams;
}

// 이니시스 폼 캐릭터 셋
const formCharset = `euc-kr`;

const InicisAccountCheck: VFC<InicisAccountCheckProps> = ({
  renderSubmitButton,
  onSuccess,
  onError,
  getParams,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const [formData, setFormData] = useState<InicisParams>();

  const start = useCallback(() => {
    if (!formRef.current) {
      onError?.(`Form Ref 찾지 못함`);
      return;
    }
    const params = getParams();
    if (!params.name) {
      onError?.(`예금주명은 필수입니다.`);
      return;
    }

    setModalOpen(true);
    const token = sessionStorage.getItem(`or.kr.unicef.path.token`) as any;


    if(token < 2){
      formRef.current.nmcomp.value = params.name;
      formRef.current.noacct.value = params.account;
    } else { //모듈은 마스킹 때문에 처리
      if(params.account.indexOf("**") < 0){
        formRef.current.noacct.value = params.account;
      } else {
          formRef.current.noacct.value = params.oriAccountNumber;
      
      } 
      
      if(params.name.indexOf("**") < 0){
        formRef.current.nmcomp.value = params.name;
      } else {
          formRef.current.nmcomp.value = params.oriName; 

      } 
    }


    formRef.current.banksett.value = INICIS_BANK_MAP[params.bankCode];

    // IE일때 charset을 덮어씌워야 정상적으로 euc-kr로 전송됨
    if (isIE()) {
      const originalCharset = document.charset;
      document.charset = formCharset;
      formRef.current?.submit();
      document.charset = originalCharset;
    } else {
      formRef.current?.submit();
    }

    setFormData(params);
  }, [getParams, onError]);

  // postMessage 리스너 등록
  useEffect(() => {
    const callback = (e: MessageEvent<InicisResult>) => {
      const { type, resultCode, resultMessage, isDecoded } = e.data;
      // 이니시스 계좌인증 이벤트가 아니라면 무시
      if (type !== `inicis-account-check`) {
        return;
      }
      setModalOpen(false);
      // 000 성공
      if (resultCode === `000`) {
        onSuccess?.(e.data);
        return;
      }
      if (resultCode === `103`) {
        onError?.(`입력한 예금주명 또는 상호명이 실계좌 예금주명과 틀립니다`);
        return;
      }
      // 에러지만 디코딩이 성공적이었다면 넘어온 메세지 출력
      if (isDecoded) {
        onError?.(resultMessage);
        return;
      }
      // 나머지는 그냥 에러
      onError?.(`계좌인증 실패`);
    };
    window.addEventListener(`message`, callback, false);
    return () => window.removeEventListener(`message`, callback);
  }, [formData?.name, onError, onSuccess]);

  if (!isBrowser) {
    return null;
  }

  return (
    <>
      {renderSubmitButton({ onClick: start })}
      {createPortal(
        <form
          ref={formRef}
          name="accountCheckForm"
          method="get"
          action="https://iniweb-api.inicis.com/DefaultWebApp/service/acct_cfrm/inicis.jsp"
          target="popupAccountChk"
          acceptCharset={formCharset}
        >
          <input type="hidden" name="nmcomp" readOnly />
          <input type="hidden" name="noacct" readOnly />
          <input type="hidden" name="banksett" readOnly />
          <input
            type="hidden"
            name="mid"
            value={process.env.INICIS_ACCOUNT_CHECK_MID || ``}
            readOnly
          />
          <input
            type="hidden"
            name="rltURL"
            value={`${process.env.SELF_URL || ``}/inicis-account-check`}
            readOnly
          />
        </form>,
        document.body,
      )}
      <IframeModal
        isOpen={modalOpen}
        name="popupAccountChk"
        maxWidth="500px"
        hideContent
        onRequestClose={() => setModalOpen(false)}
      />
    </>
  );
};

export default InicisAccountCheck;
